<template>
  <app-modal
    v-model="isDialogOpened"
    ok-only
    size="xl"
    :persistent="false"
    :ok-text="$t('project.members.dialogs.MembersSeeAllDialog.close')"
    @ok="closeDialog"
    @cancel="closeDialog"
  >
    <template #title>
      {{ $t('project.members.dialogs.MembersSeeAllDialog.title', { groupName: group.name }) }}
    </template>
    <template #body>
      <div class="d-flex align-center">
        <h4 v-t="{
            path: 'project.members.dialogs.MembersSeeAllDialog.membersCount',
            plural: group.members.length,
            args: { nbMembers: group.members.length },
          }"
        />

        <SearchTextField
          v-model="search"
          class="ml-4"
          :tooltipText="$t('project.members.dialogs.MembersSeeAllDialog.searchPlaceholder')"
        />
      </div>

      <div class="mt-6">
        <v-data-table
          :headers="headers"
          :items="sortedMembers"
          :page="pagination.currentPage"
          :items-per-page="pagination.itemsPerPage"
          :cell-props="{ class: 'body-md-regular' }"
        >
          <template #headers="{ columns }">
            <th
              v-for="column in columns"
              :key="column.value"
              class="border-b py-2 bg-grey-50"
            >
              <template v-if="column.title">
                <div
                  class="table-header clickable d-flex px-4 heading-xs-bold text-text-primary"
                  :class="{
                    'justify-center': column.center,
                    'table-header--sortable': column.sortable,
                    'table-header--sorted': sorting.name === column.value,
                  }"
                  @click="sortTable(column.value)"
                >
                  <span>{{ column.title }}</span>

                  <div v-if="column.sortable" class="sorting ml-2">
                    <app-material-icon
                      v-show="sorting.name === column.value && sorting.order === 'asc'"
                      name="arrow_downward"
                    />
                    <app-material-icon
                      v-show="sorting.name === column.value && sorting.order === 'desc'"
                      name="arrow_upward"
                    />
                    <app-material-icon
                      v-show="sorting.name !== column.value"
                      name="mobiledata_arrows"
                      size="small"
                      class="table-header__sort-icon"
                    />
                  </div>
                </div>
              </template>
            </th>
          </template>

          <template v-if="!hideStatusColumn && isCurrentUserPm" #item.status="{item}">
            <div v-if="item.isRedactedForInactivity" class="d-flex align-center">
              <div
                class="detail-md-regular ignore-draggable-elements px-2 py-1 mr-1 rounded-sm mt-1 w-100">
                {{ $t('project.members.Member.isRedactedForInactivity') }}
              </div>
            </div>
            <template v-else>
              <template v-if="item.isActive">
                <template v-if="isDataRoom">
                  <div v-if="showSignedNdaInfo(item)" class="d-flex align-center ga-1">
                    <app-material-icon
                      :name="item.hasSignedNda ? 'check_circle' : 'warning'"
                      :color="item.hasSignedNda ? 'icon-success' : 'icon-warning'"
                    />
                    <span class="detail-md-regular">
                      {{
                        item.hasSignedNda
                          ? $t('project.members.dialogs.MemberDetailsDialog.ndaSigned')
                          : $t('project.members.dialogs.MemberDetailsDialog.ndaUnsigned')
                      }}
                    </span>
                  </div>
                </template>
                <template v-else>
                  <template v-if="!item.missingInfoToSign">
                    <div class="d-flex align-center">
                      <app-material-icon
                        name="verified"
                        class="mx-1"
                        color="icon-info"
                      />
                      <span class="detail-md-regular">
                        {{ $t('project.members.dialogs.MembersSeeAllDialog.identityVerified') }}
                      </span>
                    </div>
                  </template>
                  <AppTooltip v-if="isUserMissingIdCheck(item)">
                    <template #activator="{ props }">
                      <div class="px-2 py-1 rounded-sm detail-md-regular d-flex align-center" v-bind="props">
                        <app-material-icon
                          name="id_card"
                          class="mr-1"
                        />
                        {{ $t('project.members.dialogs.MembersSeeAllDialog.identityVerificationNotPerformed') }}
                      </div>
                    </template>
                    <span> {{
                        $t('project.members.dialogs.MembersSeeAllDialog.tooltipIdentityVerificationNotPerformed', { name: item.fullName })
                      }} </span>
                  </AppTooltip>
                </template>
              </template>
              <AppTooltip v-else>
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    variant="flat"
                    class="account-not-activated-button detail-md-regular px-2 py-1 rounded-sm"
                    @click="sendEmail(item.id)"
                  >
                      <app-material-icon
                        name="mail"
                        class="mr-1"
                      />
                      {{$t('project.members.dialogs.MembersSeeAllDialog.accountNotActivated')}}
                  </v-btn>
                </template>
                <span>{{ $t('project.members.dialogs.MembersSeeAllDialog.sendFollowUpEmail') }}</span>
              </AppTooltip>
            </template>
          </template>
          <template v-if="isCurrentUserPm" #item.remove="{item}">
            <div class="d-flex justify-center">
              <MemberDeleteDialog :member="item" with-trash-icon />
            </div>
          </template>

          <template #bottom>
            <div class="d-flex justify-end mt-4">
              <AppPagination
                v-model:current-page="pagination.currentPage"
                v-model:page-size="pagination.itemsPerPage"
                :total="sortedMembers.length"
                :page-sizes="[50, 100, 250]"

              />
            </div>
          </template>
        </v-data-table>
      </div>
    </template>
  </app-modal>
</template>

<script>
import { cloneDeep } from 'lodash-es'
import { mapState, mapActions, mapGetters } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import SearchTextField from '@/common/SearchTextField.vue'
import { numericSort } from '@/common/utils/sorting'
import { toast } from '@/common/utils/toast'
import { isUserMissingIdCheck } from '@/common/utils/users'
import { getUserSetting, setUserSetting } from '@/common/utils/userSettings'
import { dialogMixin } from '@/mixins/dialogMixin'

import MemberDeleteDialog from './MemberDeleteDialog.vue'

const DEFAULT_PAGE_SIZE = 50

export default {
  name: 'MembersSeeAllDialog',
  components: {
    AppPagination,
    AppTooltip,
    MemberDeleteDialog,
    SearchTextField,
  },
  mixins: [dialogMixin],
  props: {
    group: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      search: '',
      options: {},
      sorting: {
        name: 'fullName',
        order: 'asc',
      },
      pagination: {
        currentPage: 1,
        itemsPerPage: DEFAULT_PAGE_SIZE,
      },
    }
  },
  computed: {
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['isCurrentUserPm', 'isDataRoom']),
    ...mapGetters('user', ['currentUserId']),
    searchResults () {
      if (this.search) {
        return this.group.members.filter(member => {
          let includesName; let includesCompany; let includesEmail
          if (member.fullName) {
            includesName = member.fullName.toLowerCase().includes(this.search.toLowerCase())
          }
          if (member.company) {
            includesCompany = member.company.toLowerCase().includes(this.search.toLowerCase())
          }
          if (member.email) {
            includesEmail = member.email.toLowerCase().includes(this.search.toLowerCase())
          }
          return includesName || includesCompany || includesEmail
        })
      }
      return this.group.members
    },
    sortedMembers () {
      const members = cloneDeep(this.searchResults)

      members.sort((a, b) => {
        switch (this.sorting.name) {
          case 'fullName':
          case 'email':
          case 'company': {
            return numericSort(a[this.sorting.name], b[this.sorting.name], this.sorting.order === 'desc')
          }
          case 'status': {
            const statusValue = (member) => {
              if (member.isActive && !member.missingInfoToSign) {
                return 0
              }

              if (member.isActive && this.isUserMissingIdCheck(member)) {
                return 1
              }

              return 2
            }
            const compareResult = statusValue(a) > statusValue(b) ? 1 : -1

            return this.sorting.order === 'asc' ? compareResult : compareResult * -1
          }
        }
      })

      return members
    },
    headers () {
      const columnHeaders = [
        {
          title: this.$t('project.members.dialogs.MembersSeeAllDialog.headers.name'),
          value: 'fullName',
          sortable: true,
        },
        {
          title: this.$t('project.members.dialogs.MembersSeeAllDialog.headers.mail'),
          value: 'email',
          sortable: true,
        },
        {
          title: this.$t('project.members.dialogs.MembersSeeAllDialog.headers.company'),
          value: 'company',
          sortable: true,
        },
      ]
      if (this.isCurrentUserPm) {
        if (!this.hideStatusColumn) {
          columnHeaders.push(
            {
              title: this.$t('project.members.dialogs.MembersSeeAllDialog.headers.status'),
              value: 'status',
              sortable: true,
            },
          )
        }
        columnHeaders.push(
          {
            title: this.$t('project.members.dialogs.MembersSeeAllDialog.headers.remove'),
            value: 'remove',
            sortable: false,
            center: true,
          },
        )
      }
      return columnHeaders
    },
    hideStatusColumn () {
      return this.isDataRoom && !this.currentRoom?.hasNda
    },
  },
  watch: {
    'pagination.itemsPerPage': {
      handler (itemsPerPage) {
        this.changeItemsPerPage(itemsPerPage)
      },
    },
  },
  mounted () {
    const itemsPerPageStorage = getUserSetting(this.currentUserId, 'see-all-members-items-per-page')
    const itemsPerPage = itemsPerPageStorage !== null ? parseInt(itemsPerPageStorage) : DEFAULT_PAGE_SIZE
    this.changeItemsPerPage(itemsPerPage)
  },
  methods: {
    ...mapActions('groups', ['CHASE_MEMBER']),
    isUserMissingIdCheck (user) {
      return isUserMissingIdCheck(user)
    },
    showSignedNdaInfo (user) {
      return this.currentRoom?.hasNda && user.hasOwnProperty('hasSignedNda')
    },
    sendEmail (memberId) {
      this.CHASE_MEMBER(memberId)
        .then(() => {
          toast.success(this.$t('project.members.dialogs.MembersSeeAllDialog.messages.successChaseUser'))
        })
        .catch(error => {
          if (error.response?.status === 403 && error.response?.data?.errorCode === 'ERR_WAIT_BEFORE_CHASING') {
            toast.error(this.$t('project.members.dialogs.MembersSeeAllDialog.messages.errorChaseUserWait'))
          } else if (error.response?.status === 409 && error.response?.data?.errorCode === 'ERR_USER_ALREADY_ACTIVE') {
            toast.error(this.$t('project.members.dialogs.MembersSeeAllDialog.messages.errorChaseUserAlreadyActive'))
          } else {
            toast.error(this.$t('project.members.dialogs.MembersSeeAllDialog.messages.errorChaseUser'))
          }
        })
    },
    sortTable (columnName) {
      if (this.sorting.name === columnName) {
        if (this.sorting.order === 'asc') {
          this.sorting.order = 'desc';
        } else {
          this.sorting.order = 'asc';
        }
      } else {
        this.sorting.name = columnName
        this.sorting.order = 'asc'
      }
    },
    changeItemsPerPage (itemsPerPage) {
      setUserSetting(this.currentUserId, 'see-all-members-items-per-page', itemsPerPage)

      this.pagination = {
        currentPage: 1,
        itemsPerPage: itemsPerPage,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.table-header {
  opacity: .6;
  transition: opacity 80ms ease-in-out;

  &__sort-icon {
    opacity: 0;
  }

  &:hover {
    .table-header__sort-icon {
      opacity: 1;
    }
  }
}
.table-header--sorted,
.table-header--sortable:hover{
  opacity: 1;
}

.account-not-activated-button {
  width: 100%;
  height: 30px;
  background-color: rgb(var(--v-theme-quaternary-lighten-3));
}
</style>
